import { formatApiErrorDetail } from "@/lib/api";
/**
 * AI Assessment modal — founder-only Claude pass over all punch items.
 * Shows per-item cost ranges + recommendations + a paragraph summary the
 * founder can copy/edit before pitching. Optional "apply mid-range to items"
 * toggle persists the estimate onto items with $0 cost.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Sparkle, CurrencyDollar, Copy, CheckCircle } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("access_token") || localStorage.getItem("token")}` } });

const usd = c => `$${((c || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function AIAssessModal({ projectId, projectName, items, onClose, onApplied }) {
  const [context, setContext] = useState("");
  const [applyCosts, setApplyCosts] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [applied, setApplied] = useState(0);

  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${BASE}/api/projects/${projectId}/ai-assess`,
        { context_note: context, apply_costs: applyCosts }, auth(),
      );
      setResult(r.data.assessment);
      setApplied(r.data.applied_to_items || 0);
      if (r.data.applied_to_items) {
        toast.success(`Applied estimates to ${r.data.applied_to_items} item${r.data.applied_to_items === 1 ? "" : "s"}`);
        onApplied?.();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "AI assessment failed"));
    } finally { setBusy(false); }
  };

  const copySummary = () => {
    if (!result?.summary) return;
    navigator.clipboard.writeText(result.summary);
    toast.success("Summary copied");
  };

  const perItemById = new Map();
  for (const r of (result?.items || [])) perItemById.set(r.id, r);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start md:items-center justify-center p-2 md:p-4"
      data-testid="ai-assess-modal" onClick={() => !busy && onClose()}>
      <div onClick={e => e.stopPropagation()}
        className="bg-white border-4 border-[#0f172a] shadow-[8px_8px_0px_#00C4CC] max-w-3xl w-full max-h-[95vh] overflow-auto">
        <div className="p-4 border-b-4 border-[#0f172a] flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkle weight="fill" size={22} className="text-[#00C4CC]"/>
            <div className="min-w-0">
              <div className="font-display font-black text-lg uppercase leading-tight truncate">AI Cost Estimate</div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 truncate">{projectName}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="ai-assess-close" className="text-slate-500"><X size={22}/></button>
        </div>

        {!result ? (
          <div className="p-4 space-y-3">
            <div className="text-xs text-slate-600">
              Runs your {items?.length || 0} punch items through Claude to get per-item cost ranges + recommendations + a paragraph summary. <b>Founder-only — never shown to landlord or subs.</b>
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Extra context for AI (optional)</div>
              <textarea rows={3} value={context} onChange={e => setContext(e.target.value)}
                data-testid="ai-assess-context"
                placeholder="e.g. Philly market, 20-year-old buildings, Leslie budget-conscious, prefer replace-not-repair when spring is broken."
                className="w-full border-2 border-[#0f172a] px-2 py-1 text-sm"/>
            </div>
            <label className="flex items-center gap-2 p-2 border-2 border-slate-300 cursor-pointer">
              <input type="checkbox" checked={applyCosts} onChange={e => setApplyCosts(e.target.checked)}
                data-testid="ai-assess-apply" className="h-4 w-4"/>
              <span className="text-xs">
                <span className="font-black uppercase tracking-widest text-[10px]">Apply mid-range to items with $0</span>
                <span className="text-slate-500 block">Auto-fills cost on items you haven't priced yet. Existing costs are never overwritten.</span>
              </span>
            </label>
            <button onClick={run} disabled={busy}
              data-testid="ai-assess-run"
              className="w-full h-12 bg-[#00C4CC] hover:bg-cyan-600 text-white font-black uppercase text-sm tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-40">
              <Sparkle weight="fill" size={16}/>
              {busy ? "Assessing… (30-60s)" : "Run AI Assessment"}
            </button>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <div className="p-3 bg-emerald-50 border-2 border-emerald-500">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-800 mb-1">Portfolio total (AI range)</div>
              <div className="font-display font-black text-3xl text-emerald-900">
                {usd(result.portfolio_total_low_cents)} – {usd(result.portfolio_total_high_cents)}
              </div>
              {applied > 0 && (
                <div className="mt-1 text-[10px] font-black uppercase tracking-widest text-emerald-700 inline-flex items-center gap-1">
                  <CheckCircle weight="fill" size={12}/> Applied mid-range to {applied} item{applied === 1 ? "" : "s"}
                </div>
              )}
            </div>

            <div className="p-3 bg-slate-50 border-2 border-[#0f172a]">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-800">📝 Draft summary</div>
                <button onClick={copySummary} data-testid="ai-assess-copy"
                  className="h-7 px-2 bg-[#0f172a] text-white font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1">
                  <Copy weight="bold" size={11}/> Copy
                </button>
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{result.summary}</div>
            </div>

            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Per-item estimates</div>
              <div className="space-y-1">
                {items.map(it => {
                  const ai = perItemById.get(it.id);
                  if (!ai) return null;
                  return (
                    <div key={it.id} className="p-2 bg-white border-2 border-slate-300" data-testid={`ai-item-${it.id}`}>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <div className="font-black text-sm truncate flex-1">{it.title}</div>
                        <div className="font-mono font-black text-emerald-700">
                          <CurrencyDollar weight="bold" size={12} className="inline"/>{usd(ai.cost_low_cents).replace("$","")} – {usd(ai.cost_high_cents).replace("$","")}
                        </div>
                      </div>
                      <div className="text-xs text-slate-600 italic mt-1">{ai.recommendation}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <button onClick={onClose} className="w-full h-11 bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
